export const NEWSLETTERS_GUIDE = {
  id: "newsletters",
  title: "Email Newsletters",
  description:
    "How to create, edit, preview, and send newsletters — including templates, merge tags, polls, analytics, and send channels.",
  icon: "Mail",
  version: "1.0.0",
  lastUpdated: "2026-03-11",
  requires: ["sendgrid"],
  sections: [
    /* ---------------------------------------------------------- */
    /*  Overview                                                   */
    /* ---------------------------------------------------------- */
    {
      id: "overview",
      title: "Overview",
      content: [
        {
          type: "paragraph",
          text: "The **Newsletters** module lets you compose branded HTML emails in a rich text editor, personalise them with merge tags, and send them to your contact segments. Every newsletter is built on MJML for bulletproof rendering across all major email clients.",
        },
        {
          type: "paragraph",
          text: "Key capabilities:",
        },
        {
          type: "steps",
          items: [
            "**Rich text editor** with headings, lists, images, links, buttons, YouTube embeds, and polls",
            "**Templates** — start from a blank canvas or choose a pre-built layout (Weekly Digest, Market Alert, Client Update)",
            "**Merge tags** — personalise content with the recipient's name, company, type, and more",
            "**Three send channels** — SendGrid (bulk), O365 Send (Outlook), and O365 Draft (create drafts in Outlook)",
            "**Test sends** — preview the final email in your inbox before sending to the full audience",
            "**Analytics** — track opens, clicks, click-to-open rate, forwarding, and per-link performance",
            "**Resend to unopened** — re-send to recipients who didn't open the original, with a new subject line",
          ],
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Creating a Newsletter                                      */
    /* ---------------------------------------------------------- */
    {
      id: "creating",
      title: "Creating a Newsletter",
      content: [
        {
          type: "steps",
          items: [
            "Go to **Newsletters** from the sidebar",
            "Click **New Newsletter**",
            "Choose a **template** from the Templates panel on the left — or start with **Blank**",
            "Enter a **subject line** in the left sidebar",
            "Optionally add **preview text** — this is the snippet shown in inbox previews next to the subject",
            "Select the **segment** (audience) you want to send to",
            "Compose your content in the editor",
          ],
        },
        {
          type: "callout",
          variant: "info",
          text: "The header and footer of each template are locked — you edit only the body content. This ensures consistent branding across all newsletters.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  The Editor Toolbar                                         */
    /* ---------------------------------------------------------- */
    {
      id: "editor-toolbar",
      title: "Editor Toolbar",
      content: [
        {
          type: "paragraph",
          text: "The toolbar across the top of the editor gives you access to all formatting and content insertion tools:",
        },
        {
          type: "heading",
          text: "Text Formatting",
        },
        {
          type: "table",
          headers: ["Tool", "Description"],
          rows: [
            ["**Block format**", "Switch between Paragraph, Heading 1, Heading 2, Heading 3, and Quote"],
            ["**Bold / Italic / Underline / Strikethrough**", "Standard inline formatting"],
            ["**Text colour**", "Pick from 12 brand-aligned colours"],
            ["**Alignment**", "Left, centre, or right alignment"],
            ["**Lists**", "Bulleted or numbered lists"],
            ["**Horizontal rule**", "Insert a divider line between sections"],
          ],
        },
        {
          type: "heading",
          text: "Content Insertion",
        },
        {
          type: "table",
          headers: ["Tool", "Description"],
          rows: [
            ["**Insert Link**", "Add a hyperlink — enter the URL when prompted"],
            ["**Insert Image**", "Upload an image (auto-resized to 600px max width, displayed at 66% width centred)"],
            ["**YouTube Video**", "Paste a YouTube URL to embed a clickable thumbnail with play button"],
            ["**CTA Button**", "Add a call-to-action button with custom label, URL, colours, and alignment"],
            ["**Poll**", "Insert one or more polling questions with vote buttons (see Polls section)"],
            ["**Merge Tags**", "Insert personalisation tags from the dropdown (see Merge Tags section)"],
          ],
        },
        {
          type: "callout",
          variant: "tip",
          text: "You can also insert images by **dragging and dropping** a file onto the editor, or by **pasting** an image from your clipboard.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Merge Tags                                                 */
    /* ---------------------------------------------------------- */
    {
      id: "merge-tags",
      title: "Merge Tags",
      content: [
        {
          type: "paragraph",
          text: "Merge tags are placeholders that get replaced with each recipient's personal data when the newsletter is sent. You can insert them from the **Merge Tags** dropdown in the toolbar or from the Merge Tags panel in the left sidebar.",
        },
        {
          type: "table",
          headers: ["Tag", "Replaced With", "Example"],
          rows: [
            ["`{{first_name}}`", "Recipient's first name", "Sarah"],
            ["`{{last_name}}`", "Recipient's last name", "Chen"],
            ["`{{company}}`", "Recipient's company", "Meridian Capital"],
            ["`{{email}}`", "Recipient's email address", "s.chen@meridian.com"],
            ["`{{type}}`", "Contact type (derived from tags)", "Client"],
            ["`{{unsubscribe_link}}`", "One-click unsubscribe URL", "(auto-generated)"],
            ["`{{chairmans_access_link}}`", "Chairman's Access portal link", "(auto-generated)"],
            ["`{{web_version_link}}`", "Link to web archive of this newsletter", "(auto-generated)"],
            ["`{{newsletter_view_link}}`", "Per-recipient tracked link to this newsletter (HMAC-signed)", "(auto-generated)"],
            ["`{{webcast_register:eventId}}`", "Per-recipient webcast registration link (HMAC-signed)", "(auto-generated)"],
          ],
        },
        {
          type: "callout",
          variant: "info",
          text: "Merge tags appear as highlighted badges in the editor so you can easily spot them. In the sent email, they are seamlessly replaced with real values.",
        },
        {
          type: "heading",
          text: "Webcast Registration Links",
        },
        {
          type: "paragraph",
          text: "To include a webcast registration button in your newsletter, click **Merge Tags > Webcast Registration Link** in the toolbar. You will be prompted to select from upcoming webcasts. A styled \"Register\" button is inserted into the newsletter.",
        },
        {
          type: "paragraph",
          text: "When the newsletter is sent, each recipient receives a unique HMAC-signed registration link pre-filled with their email address — the same secure tokens used by the invitation system. Clicking the link takes the recipient directly to the registration page with their details already filled in.",
        },
        {
          type: "callout",
          variant: "tip",
          text: "This is ideal for promoting webcasts in your regular newsletters without having to send separate invitations. Recipients who register via the newsletter link are tracked the same as those invited directly.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Templates                                                  */
    /* ---------------------------------------------------------- */
    {
      id: "templates",
      title: "Templates",
      content: [
        {
          type: "paragraph",
          text: "Templates provide pre-designed layouts with locked headers and footers. Choose one from the **Templates** panel when creating a newsletter:",
        },
        {
          type: "table",
          headers: ["Template", "Best For"],
          rows: [
            ["**Blank**", "Starting from scratch with just a header and footer"],
            ["**Weekly Digest**", "Regular briefings with sections for key developments, dashboard highlights, and upcoming events"],
            ["**Market Alert**", "Urgent alerts with a red banner, situation summary, and recommended actions"],
            ["**Client Update**", "Monthly updates with executive summary, watchlist, and action items"],
          ],
        },
        {
          type: "callout",
          variant: "tip",
          text: "The body content loaded by a template is just a starting point — you can edit, delete, or rearrange any of it. Only the header and footer are locked.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Polls                                                      */
    /* ---------------------------------------------------------- */
    {
      id: "polls",
      title: "Polls",
      content: [
        {
          type: "paragraph",
          text: "Polls let you embed interactive voting questions directly in your newsletter. Recipients click a button to vote, and results are tallied automatically.",
        },
        {
          type: "heading",
          text: "Inserting a Poll",
        },
        {
          type: "steps",
          items: [
            "Click the **Poll** button in the toolbar",
            "Enter how many polling questions you want to add (you can add multiple at once)",
            "For each question, enter the **polling question** text",
            "Enter the **number of options** (minimum 2)",
            "Type each **option** label when prompted",
            "Choose whether to include a **free-text response** option — this adds an extra button that links to a form where recipients can type their own answer",
          ],
        },
        {
          type: "heading",
          text: "How Polls Work in Email",
        },
        {
          type: "steps",
          items: [
            "Each option renders as a **clickable button** in the email",
            "When a recipient clicks a vote button, they are taken to a **results page** showing a live bar chart of all votes",
            "Each recipient can only vote once per poll (deduplicated by email address)",
            "If free-text is enabled, an additional outline-styled button links to a **response form** where the recipient can type their answer",
            "Free-text responses appear in a separate section on the results page",
          ],
        },
        {
          type: "heading",
          text: "Editing a Poll",
        },
        {
          type: "paragraph",
          text: "In the editor, hover over a poll to reveal the **edit** (pencil) and **delete** (X) buttons. Click edit to change the question, options, or free-text setting.",
        },
        {
          type: "callout",
          variant: "info",
          text: "Polls are rendered as compact, email-safe buttons that work in all major email clients including Outlook desktop. No JavaScript is required — everything works via simple links.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  CTA Buttons                                                */
    /* ---------------------------------------------------------- */
    {
      id: "cta-buttons",
      title: "CTA Buttons",
      content: [
        {
          type: "paragraph",
          text: "Call-to-action buttons let you add prominent, clickable buttons to your newsletter. They render as bulletproof email buttons that work in all clients, including Outlook desktop.",
        },
        {
          type: "steps",
          items: [
            "Click the **CTA Button** tool in the toolbar",
            "Enter the **button label** (e.g. \"Register Now\")",
            "Enter the **destination URL**",
            "Optionally customise the **background colour**, **text colour**, and **alignment** (left, centre, right)",
          ],
        },
        {
          type: "paragraph",
          text: "In the editor, hover over a button to reveal edit and delete controls, just like polls.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  YouTube Embeds                                             */
    /* ---------------------------------------------------------- */
    {
      id: "youtube-embeds",
      title: "YouTube Embeds",
      content: [
        {
          type: "paragraph",
          text: "You can embed YouTube videos as clickable thumbnails with a play button overlay. Since email clients don't support embedded video, this renders as a static image that links to the YouTube video.",
        },
        {
          type: "steps",
          items: [
            "Click the **YouTube** button in the toolbar",
            "Paste the full YouTube URL (e.g. `https://www.youtube.com/watch?v=abc123`)",
            "The editor shows a preview with the video thumbnail and a play button",
            "In the sent email, clicking the thumbnail opens the video in the recipient's browser",
          ],
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Preview & Test Send                                        */
    /* ---------------------------------------------------------- */
    {
      id: "preview-test",
      title: "Previewing & Test Sends",
      content: [
        {
          type: "heading",
          text: "Preview Modes",
        },
        {
          type: "paragraph",
          text: "Use the preview tabs above the editor to check how your newsletter will look:",
        },
        {
          type: "table",
          headers: ["Mode", "Description"],
          rows: [
            ["**Edit**", "The live editor view where you write and format content"],
            ["**Desktop**", "Preview at 640px width — simulates desktop email clients"],
            ["**Mobile**", "Preview at 375px width — simulates mobile email clients"],
          ],
        },
        {
          type: "heading",
          text: "Test Send",
        },
        {
          type: "paragraph",
          text: "Click **Send Test** to send the newsletter to your own email address. The subject line is prefixed with `[TEST]` so you can easily spot it. This lets you check the final rendering, merge tags (replaced with test values), and overall layout in a real email client before sending to your audience.",
        },
        {
          type: "callout",
          variant: "tip",
          text: "Always send a test before your first real send — especially when using new templates, images, or polls. Check it on both desktop and mobile if possible.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Pre-Send Validation                                        */
    /* ---------------------------------------------------------- */
    {
      id: "validation",
      title: "Pre-Send Validation",
      content: [
        {
          type: "paragraph",
          text: "Before sending, the platform runs automatic checks on your newsletter and flags any issues:",
        },
        {
          type: "heading",
          text: "Errors (block sending)",
        },
        {
          type: "steps",
          items: [
            "**Empty subject line** — every newsletter needs a subject",
            "**Empty or too-short body** — the content must have meaningful text",
            "**No recipients** — the selected segment must contain at least one contact",
          ],
        },
        {
          type: "heading",
          text: "Warnings (advisory)",
        },
        {
          type: "steps",
          items: [
            "**Subject line over 60 characters** — may get truncated in inbox previews",
            "**ALL CAPS or excessive punctuation in subject** — can trigger spam filters",
            "**Spam trigger words** detected in the subject or body",
            "**More than 8 images** — can affect load times and deliverability",
            "**Email size over 90 KB** — Gmail clips emails larger than 102 KB",
            "**Missing preview text** — the preview snippet helps open rates",
          ],
        },
        {
          type: "callout",
          variant: "info",
          text: "The editor also highlights potential spam trigger words with a wavy red underline as you type, so you can spot them early.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Send Channels                                              */
    /* ---------------------------------------------------------- */
    {
      id: "send-channels",
      title: "Send Channels",
      content: [
        {
          type: "paragraph",
          text: "When you click **Send Newsletter**, you choose how to deliver it:",
        },
        {
          type: "table",
          headers: ["Channel", "Description", "Best For"],
          rows: [
            ["**SendGrid**", "Bulk email service — fast delivery, open/click tracking, bounce handling", "Large audiences, marketing campaigns"],
            ["**O365 Send**", "Sends directly from your Outlook mailbox via Microsoft Graph", "Smaller audiences, personal touch, replies go to your inbox"],
            ["**O365 Draft**", "Creates draft emails in your Outlook Drafts folder — you review and send manually", "Maximum control, reviewing each email before it goes out"],
          ],
        },
        {
          type: "callout",
          variant: "tip",
          text: "O365 channels are throttled (one email every 2 seconds) to respect Microsoft rate limits. For large audiences, SendGrid is significantly faster.",
        },
        {
          type: "heading",
          text: "Send As (O365 only)",
        },
        {
          type: "paragraph",
          text: "When using O365 Send or Draft, you can choose which mailbox to send from. This is useful if you have delegate access to a shared or team mailbox.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Spacing Settings (Admin)                                   */
    /* ---------------------------------------------------------- */
    {
      id: "spacing",
      title: "Newsletter Spacing (Admin)",
      content: [
        {
          type: "paragraph",
          text: "Admins can fine-tune the vertical spacing between headings and paragraphs in newsletters. These settings affect both the editor preview and the final sent email.",
        },
        {
          type: "steps",
          items: [
            "Go to **Settings** from the sidebar",
            "Scroll to the **Newsletter Spacing** section (admin only)",
            "Adjust the pixel values for **H1**, **H2**, **H3** (above and below), and **Paragraph** (below) spacing",
            "Click **Save Spacing** to apply",
            "Use **Reset Defaults** to restore the original values",
          ],
        },
        {
          type: "callout",
          variant: "info",
          text: "Spacing changes take effect immediately in the editor and apply to all future sends. Previously sent newsletters are not affected.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Analytics                                                  */
    /* ---------------------------------------------------------- */
    {
      id: "analytics",
      title: "Newsletter Analytics",
      content: [
        {
          type: "paragraph",
          text: "After sending a newsletter, click **View Analytics** in the newsletter list to see detailed engagement data:",
        },
        {
          type: "heading",
          text: "Summary Metrics",
        },
        {
          type: "table",
          headers: ["Metric", "Description"],
          rows: [
            ["**Total Sent**", "Number of emails successfully delivered"],
            ["**Unique Opens**", "Number of distinct recipients who opened the email"],
            ["**Open Rate**", "Percentage of recipients who opened"],
            ["**Unique Clicks**", "Number of distinct recipients who clicked a link"],
            ["**Click Rate**", "Percentage of recipients who clicked"],
            ["**Click-to-Open Rate**", "Of those who opened, what percentage also clicked"],
          ],
        },
        {
          type: "heading",
          text: "Engagement Depth",
        },
        {
          type: "paragraph",
          text: "A visual bar breaks down your audience into three groups: **No Open** (didn't engage), **Opened Only** (read but didn't click), and **Opened + Clicked** (fully engaged). This helps you understand the quality of engagement beyond just open rates.",
        },
        {
          type: "heading",
          text: "Link Performance",
        },
        {
          type: "paragraph",
          text: "See which links in your newsletter got the most clicks. The table shows each URL with its total clicks and unique clickers — useful for understanding what content resonated.",
        },
        {
          type: "heading",
          text: "Top Engaged Contacts",
        },
        {
          type: "paragraph",
          text: "A ranked list of the most engaged recipients showing their opens, clicks, and whether they may have forwarded the email to others.",
        },
        {
          type: "heading",
          text: "Forward Detection",
        },
        {
          type: "paragraph",
          text: "The platform can detect when a newsletter was likely forwarded — if the same tracking pixel is opened from many different IP addresses, it flags the recipient as a probable forwarder and estimates the extra readership.",
        },
        {
          type: "heading",
          text: "Engagement Timeline",
        },
        {
          type: "paragraph",
          text: "A reverse-chronological event log showing every open and click event — who did what and when.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Newsletter Archive & Public View Links                     */
    /* ---------------------------------------------------------- */
    {
      id: "archive-view-links",
      title: "Newsletter Archive & Public View Links",
      content: [
        {
          type: "paragraph",
          text: "Every newsletter that is successfully sent is automatically archived. You can browse past newsletters in the **Newsletter Archive** (accessible from the Newsletters page) and share them via public links.",
        },
        {
          type: "heading",
          text: "Linking to Past Newsletters",
        },
        {
          type: "paragraph",
          text: "You can link to any archived newsletter from within a new newsletter. This is useful for referencing previous analysis, including a \u201Cin case you missed it\u201D section, or building a curated reading list.",
        },
        {
          type: "steps",
          items: [
            "Open the **Newsletter Archive** and find the newsletter you want to link to",
            "Click the **Copy link** button (appears when the newsletter has a public token)",
            "In the newsletter editor, select the text you want to make into a link",
            "Paste the copied URL as the link destination",
          ],
        },
        {
          type: "heading",
          text: "Per-Recipient View Tracking",
        },
        {
          type: "paragraph",
          text: "For newsletters sent via the system, each recipient gets a unique tracked link via the `{{newsletter_view_link}}` merge tag. When a recipient clicks this link, the view is recorded and attributed to them. You can see who viewed the newsletter and how many times in the archive modal.",
        },
        {
          type: "callout",
          variant: "info",
          text: "The tracked link uses HMAC-signed tokens — it cannot be faked or tampered with. If a recipient forwards the email, views from the forwarded copy are attributed to the original recipient.",
        },
        {
          type: "heading",
          text: "View Analytics",
        },
        {
          type: "paragraph",
          text: "In the Newsletter Archive, click any newsletter to open its detail modal. If the newsletter has view tracking enabled, you\u2019ll see:",
        },
        {
          type: "table",
          headers: ["Metric", "Description"],
          rows: [
            ["**Unique Viewers**", "Number of distinct recipients who clicked through to view"],
            ["**Total Views**", "Total number of views (includes repeat visits)"],
          ],
        },
        {
          type: "callout",
          variant: "tip",
          text: "Imported GHL newsletters can also have public tokens added to make them shareable. These won\u2019t have per-recipient tracking (since they were sent outside the system) but can still be linked in future newsletters.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Resend to Unopened                                         */
    /* ---------------------------------------------------------- */
    {
      id: "resend-unopened",
      title: "Resend to Unopened",
      content: [
        {
          type: "paragraph",
          text: "If a newsletter had a low open rate, you can re-send it to recipients who didn't open the original. This is a common tactic to boost engagement by trying a different subject line.",
        },
        {
          type: "steps",
          items: [
            "In the **Newsletters** list, find the sent newsletter",
            "Click the **Resend to Unopened** action",
            "Enter a **new subject line** (prefixed automatically with the original if desired)",
            "Choose the **send channel** (SendGrid, O365 Send, or O365 Draft)",
            "Click **Resend** to send only to contacts who have not yet opened the email",
          ],
        },
        {
          type: "callout",
          variant: "info",
          text: "The resend uses the same content and personalisation as the original — only the subject line and send method can be changed. Analytics for the resend are tracked separately.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Unsubscribe & Deliverability                               */
    /* ---------------------------------------------------------- */
    {
      id: "deliverability",
      title: "Unsubscribe & Deliverability",
      content: [
        {
          type: "heading",
          text: "Unsubscribe",
        },
        {
          type: "paragraph",
          text: "Every newsletter includes an unsubscribe link in the footer (via the `{{unsubscribe_link}}` merge tag). When a recipient clicks it, they are taken to a confirmation page and removed from future mailings. Unsubscribed contacts are automatically excluded from all newsletter sends.",
        },
        {
          type: "heading",
          text: "Bounce Handling",
        },
        {
          type: "paragraph",
          text: "When using SendGrid, bounced emails are tracked automatically. Hard bounces (invalid addresses) are flagged so you can clean your contact list. You can monitor deliverability in the **Email Health** dashboard.",
        },
        {
          type: "heading",
          text: "Email Health Dashboard",
        },
        {
          type: "paragraph",
          text: "The **Email Health** page (accessible from the sidebar) shows your SPF, DKIM, and DMARC authentication status, recent bounces, spam reports, and overall deliverability score. Green indicators mean your email authentication is properly configured.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Troubleshooting                                            */
    /* ---------------------------------------------------------- */
    {
      id: "troubleshooting",
      title: "Troubleshooting",
      content: [
        {
          type: "faq",
          items: [
            {
              question: "My newsletter looks different in Outlook than in the preview",
              answer:
                "Outlook desktop has limited CSS support. The platform uses VML fallbacks and inline styles for maximum compatibility, but some differences are expected. YouTube embeds render as static thumbnails, and CTA buttons use VML roundrects. Always send a test to check Outlook rendering.",
            },
            {
              question: "Images aren't centred in the editor",
              answer:
                "Images should display at 66% width and centred, matching the email output. If they appear full-width, try removing and re-inserting the image. The editor CSS centres all images by default.",
            },
            {
              question: "The email is being clipped in Gmail",
              answer:
                "Gmail clips emails larger than 102 KB. The editor shows a size estimate in the bottom bar and warns you if you're approaching the limit. To reduce size: remove unnecessary images, simplify formatting, or split the content into multiple newsletters.",
            },
            {
              question: "Merge tags aren't being replaced",
              answer:
                "Make sure you're using the correct tag format: `{{first_name}}`, `{{last_name}}`, etc. Tags inserted from the toolbar dropdown are always correct. If you typed a tag manually, check for typos or extra spaces inside the braces.",
            },
            {
              question: "O365 send is slow",
              answer:
                "O365 sends are throttled to one email every 2 seconds to stay within Microsoft's rate limits. For an audience of 100 contacts, this takes about 3-4 minutes. For large audiences, use SendGrid instead.",
            },
            {
              question: "There's no gap between the newsletter and my Outlook signature",
              answer:
                "When sending via O365, Outlook automatically appends your email signature. The platform adds a spacer between the newsletter footer and the signature, but if your signature has minimal top padding, it may still appear close. This is controlled by your Outlook signature settings.",
            },
            {
              question: "Poll votes aren't showing up",
              answer:
                "Poll results are tallied in real time. Make sure the recipient actually clicked a vote button (not just opened the email). Each person can only vote once per poll — duplicate votes from the same email address are silently ignored.",
            },
            {
              question: "I can't see the Send button",
              answer:
                "You must **save the newsletter as a draft** first before the send option appears. Click Save Draft, then click Send Newsletter.",
            },
            {
              question: "The resend option isn't available",
              answer:
                "Resend to Unopened is only available for newsletters that have been fully sent (status: **sent**). Drafts and newsletters still in the sending state cannot be resent.",
            },
          ],
        },
      ],
    },
  ],
};
